// @ts-nocheck
import {
  ComparatorContext,
  Step,
  Transformers,
  parseTransformer,
  useField,
  usePage,
  useStorage,
  useTransformers,
  useWizardService,
} from '@kizi/comparator-core';
import React, { useContext, useEffect, useMemo } from 'react';
import { parseProductFieldData } from '../../../utils/product';
import { PageLayout, PageLayoutProps } from '../../PageLayout';
import { LoadingPage, LoadingPageProps } from './LoadingPage';

export interface ProviderResponse {
  data?: object[] | [];
  meta?: object | {};
  orderCompletionText?: string | '';
  statusCode: number;
}

export type bodyObject = {
  id?: string | number;
};

export interface CheckoutPageProps extends PageLayoutProps {
  url: string;
  wizardStepsField: string;
  productFieldName: string;
  orderEndpoint: { url: string; options: RequestInit };
  replaceFieldNames?: Record<string, string>;
  defaultValues?: { [attr: string]: Transformers };
  loadingPage: LoadingPageProps;
}
// Constants
const PROVIDER_IDS = {
  OMINIMO: '1139',
} as const;

// Utility functions for Ominimo handling
const getOminimoProduct = () => {
  try {
    const stored = localStorage.getItem('ominimoProduct');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error parsing ominimoProduct from localStorage:', error);
    return null;
  }
};

const isOminimoProduct = (product: any): boolean => {
  return product?.provider?.id === PROVIDER_IDS.OMINIMO;
};

const shouldUseOminimoProductId = (
  product: any,
  name: string,
  ominimoProduct: any,
): boolean => {
  return (
    isOminimoProduct(product) && name.startsWith('car') && ominimoProduct?.id
  );
};

const transformOminimoProducts = (
  originalProducts: any,
  ominimoProductId: string,
) => {
  if (!originalProducts || typeof originalProducts !== 'object') {
    return originalProducts;
  }

  const originalKey = Object.keys(originalProducts)[0];
  if (!originalKey) {
    return originalProducts;
  }

  return {
    [ominimoProductId]: originalProducts[originalKey],
  };
};

export const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};
export const getCookie = (name) => {
  const cookieArr = document.cookie.split(';');
  for (let i = 0; i < cookieArr.length; i++) {
    const cookiePair = cookieArr[i].split('=');
    if (name === cookiePair[0].trim()) {
      try {
        return JSON.parse(decodeURIComponent(cookiePair[1]));
      } catch (e) {
        console.error('Error parsing cookie:', e);
        return null;
      }
    }
  }
  return null;
};

// LocalStorage utilities for carPremium data
const getCarPremiumData = () => {
  try {
    const stored = localStorage.getItem('carPremium');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error parsing carPremium from localStorage:', error);
    return null;
  }
};

const setCarPremiumData = (data) => {
  try {
    localStorage.setItem('carPremium', JSON.stringify(data));
  } catch (error) {
    console.error('Error storing carPremium in localStorage:', error);
  }
};

const removeCarPremiumData = () => {
  try {
    localStorage.removeItem('carPremium');
  } catch (error) {
    console.error('Error removing carPremium from localStorage:', error);
  }
};

const getBodyObject = ({
  steps,
  replaceFieldNames,
  defaultValues,
  transformers,
}) => {
  const bodyObject = {};
  for (const step of steps) {
    for (const field of step.fields) {
      const fieldName = replaceFieldNames[field.name] ?? field.name;
      let value = field.value !== undefined ? field.value : '';
      if (value === 'default') value = true;
      if (Object.keys(defaultValues).includes(fieldName)) {
        const transformersFn = parseTransformer(
          defaultValues[fieldName],
          transformers,
        );
        for (const { transformerFn } of transformersFn) {
          value = transformerFn(value);
        }
      }
      bodyObject[fieldName] = value;
    }
  }
  return bodyObject;
};

const cleanStorage = ({ getStep, setStep, wizard, name }) => {
  localStorage.removeItem(`kizi-comparator-${name}`);
  localStorage.removeItem('apollo-cache-persist');
  localStorage.removeItem('lastProductId');
  const persistedApplyStep = getStep('applyPage') as Step;
  const persistedOverviewStep = getStep('overview') as Step;
  if (!persistedApplyStep || !persistedOverviewStep) return;
  const updatedApplyStep: Step = {
    ...persistedApplyStep,
    fields: persistedApplyStep.fields.map((f) => ({
      ...f,
      value: undefined,
    })),
  };
  const updatedOverviewStep: Step = {
    ...persistedOverviewStep,
    fields: persistedOverviewStep.fields.map((f) =>
      f.name === 'selectedProduct'
        ? {
            ...f,
            value: undefined,
          }
        : f,
    ),
  };
  wizard?.send('SET_FORM_FIELDS', {
    name: 'applyPage',
    fields: updatedApplyStep.fields,
  });
  setStep('applyPage', updatedApplyStep);
  setStep('overview', updatedOverviewStep);
};

type BodyObjectType = {
  [key: string]: any;
};

export const CheckoutPage: React.FC<CheckoutPageProps> = ({
  wizardStepsField,
  productFieldName,
  orderEndpoint,
  loadingPage,
  defaultValues = {},
  replaceFieldNames = {},
  ...pageLayoutProps
}) => {
  const { state, setState } = usePage();
  const wizardService = useWizardService();
  const { name } = useContext(ComparatorContext);
  const applyWizardSteps = useField(wizardStepsField);
  const productField = useField(productFieldName);
  const cbLoggedInField = useField('cbLoggedIn');
  const isCbLoggedIn = cbLoggedInField?.value === 1;
  const product = useMemo(
    () => parseProductFieldData(productField?.value),
    [productField?.value],
  );
  const transformers = useTransformers() ?? {};
  const { getStep, setStep } = useStorage();
  const wizard = useWizardService();
  const carPremiumData = getCarPremiumData();

  // Ominimo product handling
  const ominimoProduct = getOminimoProduct();
  const isCurrentProductOminimo = isOminimoProduct(product);

  // Prepare products with Ominimo transformations if needed
  let productsToStringify = product.products ?? '';
  if (isCurrentProductOminimo && ominimoProduct?.id && product.products) {
    productsToStringify = transformOminimoProducts(
      product.products,
      ominimoProduct.id,
    );
  }

  let bodyObject: BodyObjectType = {
    ...getBodyObject({
      steps: applyWizardSteps?.value ?? [],
      replaceFieldNames,
      defaultValues,
      transformers,
    }),
    products: JSON.stringify(productsToStringify),
    ...(['energy', 'energy-auction'].includes(name)
      ? { is_auction_product: product?.is_auction_product ?? false }
      : {}),
    ...(carPremiumData?.hasPremiumChanged && name.startsWith('car')
      ? carPremiumData
      : {}),
  };

  if (bodyObject['accept_switch_fine'] === 'true') {
    bodyObject['enddatepreviouscontract'] = '';
  }

  if (!bodyObject?.startdate) {
    const date = new Date();
    bodyObject.startdate = `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  }

  bodyObject['referer'] = window.navigator.userAgent.includes('CBAPP')
    ? 'CBAPP'
    : bodyObject['referer'] || '';

  bodyObject['theftProtectionClass'] = bodyObject.car_security_class
    ? bodyObject.car_security_class[7]
    : '1';

  if (isCbLoggedIn) {
    const b64 = localStorage.getItem('SCW_LOGIN_ACCESS_TOKEN');
    if (b64) {
      const back = atob(b64.split('.')[1]);
      bodyObject['is_cb_member'] = 'Ja';
      bodyObject['membership_cb_email'] = JSON.parse(back).sub;
    }
  }

  let bodyURI =
    Object.keys(bodyObject)
      .map(
        (item) =>
          encodeURIComponent(item) + '=' + encodeURIComponent(bodyObject[item]),
      )
      .join('&') || '';

  let isResponseOk;

  useEffect(() => {
    if (localStorage.getItem('orderCompleted') === 'true') {
      localStorage.removeItem('orderCompleted');
      wizardService?.send({ type: 'GOTO', index: 1, meta: { reload: true } });
    } else {
      fetch(
        orderEndpoint.url.replace(
          ':productId',
          shouldUseOminimoProductId(product, name, ominimoProduct)
            ? ominimoProduct.id
            : product?.id,
        ) +
          '?providerId=' +
          product?.provider?.id,
        {
          ...orderEndpoint.options,
          body: bodyURI,
        },
      )
        .then((resultData) => {
          isResponseOk = resultData.ok;

          return resultData.json();
        })
        .then((bodyJson) => {
          let providerRes = {};
          if (bodyJson) {
            providerRes = Array.isArray(bodyJson)
              ? (bodyJson[0] as ProviderResponse)
              : (bodyJson as ProviderResponse);
          }
          setState?.({ ...state, providerRes, isResponseOk });
          cleanStorage({ getStep, setStep, wizard, name });
          if (name.startsWith('car')) {
            removeCarPremiumData();
          }
        })
        .catch(() =>
          setState?.({ ...state, pageError: 'something went wrong' }),
        );
    }
  }, [orderEndpoint, product, setState]);

  if (!setState || !applyWizardSteps) return null;
  if (!state?.providerRes) return <LoadingPage {...loadingPage} />;

  return (
    <PageLayout
      contentStyles={{
        mx: 'auto',
        span: [12, 12, 12, 12],
        width: [null, null, null, 810],
      }}
      {...pageLayoutProps}
    />
  );
};
